import { INotificationLanguageTemplateDto } from "@kuika/kuika-cl-model/src/dtos/notification-template.dto";
import React from "react";
import { getLanguageFlagSrc } from "../../../shared/utility/designer-languages";
import KuikaPopover from "../../ui-kit/kuika-popover/kuika-popover";

/**
 * The Languages column: an overlapping stack of flags with the full list on hover, the
 * same shape the project list uses for the people a project is shared with. A count alone
 * ("3 languages") made you open the template to find out WHICH three.
 */

export interface IEmailTemplateLanguagesCellProps {
  multiLanguageEnabled?: boolean;
  languageTemplates?: INotificationLanguageTemplateDto[];
}

/** How many flags sit in the row before the rest collapse into a +N chip. */
const VISIBLE_FLAGS = 4;

interface ILanguageChipProps {
  name: string;
  shortName: string;
  size: number;
}

const LanguageChip = ({ name, shortName, size }: ILanguageChipProps): JSX.Element => {
  const flagSrc: string | undefined = getLanguageFlagSrc(shortName);
  if (!flagSrc) {
    // A locale the catalog does not know (an older project, a hand-written row): fall back
    // to its code rather than dropping the entry.
    return (
      <span
        className="kuika-email-languages-cell__chip kuika-email-languages-cell__chip--code"
        style={{ width: size, height: size }}
        title={name}
      >
        {shortName.slice(0, 2).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      className="kuika-email-languages-cell__chip"
      style={{ width: size, height: size }}
      src={flagSrc}
      alt=""
      aria-hidden="true"
    />
  );
};

// Open state is local so hovering never re-renders the list — a parent re-render would
// hand KuikaListTable a new data array and reset pagination to page 1.
export const EmailTemplateLanguagesCell = ({
  multiLanguageEnabled,
  languageTemplates
}: IEmailTemplateLanguagesCellProps): JSX.Element => {
  const [open, setOpen] = React.useState<boolean>(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = (): void => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openNow = (): void => {
    cancelClose();
    setOpen(true);
  };
  // A grace period so the pointer can travel from the stack into the popover.
  const scheduleClose = (): void => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, 120);
  };
  React.useEffect(() => () => cancelClose(), []);

  const languages: INotificationLanguageTemplateDto[] = languageTemplates ?? [];
  if (!multiLanguageEnabled || languages.length === 0) {
    return <span className="kuika-email-languages-cell__single">Single</span>;
  }

  const sorted: INotificationLanguageTemplateDto[] = [...languages].sort(
    (a: INotificationLanguageTemplateDto, b: INotificationLanguageTemplateDto): number =>
      (a.templateLanguageName ?? "").localeCompare(b.templateLanguageName ?? "")
  );
  const visible: INotificationLanguageTemplateDto[] = sorted.slice(0, VISIBLE_FLAGS);
  const overflow: number = sorted.length - visible.length;

  return (
    <KuikaPopover
      position="top"
      hideCloseIcon
      collisionPadding={16}
      visible={open}
      onVisibleChange={(next: boolean) => setOpen(next)}
      content={
        <div className="kuika-email-languages-cell__list" onMouseEnter={openNow} onMouseLeave={scheduleClose}>
          {sorted.map((language: INotificationLanguageTemplateDto) => (
            <div key={language.id} className="kuika-email-languages-cell__list-item">
              <LanguageChip
                name={language.templateLanguageName}
                shortName={language.templateLanguageShortName}
                size={24}
              />
              <span className="kuika-email-languages-cell__list-name">{language.templateLanguageName}</span>
              <span className="kuika-email-languages-cell__list-code">{language.templateLanguageShortName}</span>
            </div>
          ))}
        </div>
      }
    >
      <span className="kuika-email-languages-cell" onMouseEnter={openNow} onMouseLeave={scheduleClose}>
        {visible.map((language: INotificationLanguageTemplateDto) => (
          <LanguageChip
            key={language.id}
            name={language.templateLanguageName}
            shortName={language.templateLanguageShortName}
            size={20}
          />
        ))}
        {overflow > 0 && (
          <span
            className="kuika-email-languages-cell__chip kuika-email-languages-cell__chip--more"
            style={{ width: 20, height: 20 }}
          >
            +{overflow}
          </span>
        )}
      </span>
    </KuikaPopover>
  );
};
